import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  WalletCategory,
  WalletCategoryI18nKeys,
  WalletCreate,
} from "@/domains/wallet";
// components
import { Button, Form, Input, Modal, Select } from "antd";
import { PlusCircleTwoTone } from "@ant-design/icons";

export const CreateModal = () => {
  const { t } = useTranslation();

  const [form] = Form.useForm<WalletCreate>();

  const [isOpen, setIsOpen] = useState(false);

  const walletOptions = useMemo(
    () =>
      Object.entries(WalletCategoryI18nKeys)
        .filter(
          ([value]) =>
            ![
              WalletCategory.SystemReceive,
              WalletCategory.SystemCollect,
              WalletCategory.SystemExpend,
              WalletCategory.ProviderReceive,
              WalletCategory.ProviderCollect,
              WalletCategory.ProviderExpend,
              WalletCategory.External,
            ].includes(Number(value))
        )
        .map(([value, ii8nKey]) => ({
          label: t(ii8nKey),
          value: Number(value),
        })),
    [t]
  );

  const handleShow = () => {
    setIsOpen(true);
    form.resetFields();
  };

  const handleConfirm = async () => {
    const formValue = await form.validateFields();
    console.log(formValue);
    setIsOpen(false);
  };

  const handleCancel = () => {
    setIsOpen(false);
  };

  return (
    <>
      <Button type="primary" icon={<PlusCircleTwoTone />} onClick={handleShow}>
        {t("domains.Wallet.createWallet")}
      </Button>

      <Modal
        title={t("domains.Wallet.createWallet")}
        open={isOpen}
        footer={[
          <div className="flex jc:end gap:8" key="footer">
            <Button key="back" onClick={handleCancel}>
              {t("systems.Action.cancel")}
            </Button>
            <Button key="submit" type="primary" onClick={handleConfirm}>
              {t("systems.Action.submit")}
            </Button>
          </div>,
        ]}
        onCancel={handleCancel}
      >
        <Form className="mt:16" form={form} labelCol={{ span: 6 }}>
          <Form.Item
            label={t("domains.Wallet.category")}
            name="category"
            initialValue={WalletCategory.OperatorReceive}
            rules={[
              {
                required: true,
                message: t("systems.Error.cannotBeEmpty", {
                  field: t("domains.Wallet.category"),
                }),
              },
            ]}
          >
            <Select
              allowClear
              placeholder={t("systems.Placeholder.pleaseSelect", {
                field: t("domains.Wallet.category"),
              })}
              options={walletOptions}
            ></Select>
          </Form.Item>

          <Form.Item
            label={t("domains.Wallet.currency")}
            name="currency"
            initialValue="USDT"
            rules={[
              {
                required: true,
                message: t("systems.Error.cannotBeEmpty", {
                  field: t("domains.Wallet.currency"),
                }),
              },
            ]}
          >
            <Select
              allowClear
              placeholder={t("systems.Placeholder.pleaseSelect", {
                field: t("domains.Wallet.currency"),
              })}
              options={[{ label: "USDT", value: "USDT" }]}
            ></Select>
          </Form.Item>

          <Form.Item
            label={t("domains.Wallet.address")}
            name="address"
            rules={[
              {
                required: true,
                message: t("systems.Error.cannotBeEmpty", {
                  field: t("domains.Wallet.address"),
                }),
              },
            ]}
          >
            <Input
              placeholder={t("systems.Placeholder.pleaseInput", {
                field: t("domains.Wallet.address"),
              })}
            />
          </Form.Item>

          <Form.Item
            label={t("domains.Wallet.balanceMax")}
            name="balance_max"
            rules={[
              {
                required: true,
                message: t("systems.Error.cannotBeEmpty", {
                  field: t("domains.Wallet.balanceMax"),
                }),
              },
            ]}
            extra={t("systems.Extra.noLimitIfZero")}
          >
            <Input
              type="number"
              placeholder={t("systems.Placeholder.pleaseInput", {
                field: t("domains.Wallet.balanceMax"),
              })}
            />
          </Form.Item>

          <Form.Item
            label={t("domains.Wallet.dailyDepositMax")}
            name="daily_deposit_max"
            rules={[
              {
                required: true,
                message: t("systems.Error.cannotBeEmpty", {
                  field: t("domains.Wallet.dailyDepositMax"),
                }),
              },
            ]}
            extra={t("systems.Extra.noLimitIfZero")}
          >
            <Input
              type="number"
              placeholder={t("systems.Placeholder.pleaseInput", {
                field: t("domains.Wallet.dailyDepositMax"),
              })}
            />
          </Form.Item>

          <Form.Item
            label={t("domains.Wallet.weeklyDepositMax")}
            name="weekly_deposit_max"
            rules={[
              {
                required: true,
                message: t("systems.Error.cannotBeEmpty", {
                  field: t("domains.Wallet.weeklyDepositMax"),
                }),
              },
            ]}
            extra={t("systems.Extra.noLimitIfZero")}
          >
            <Input
              type="number"
              placeholder={t("systems.Placeholder.pleaseInput", {
                field: t("domains.Wallet.weeklyDepositMax"),
              })}
            />
          </Form.Item>

          <Form.Item
            label={t("domains.Wallet.monthlyDepositMax")}
            name="monthly_deposit_max"
            rules={[
              {
                required: true,
                message: t("systems.Error.cannotBeEmpty", {
                  field: t("domains.Wallet.monthlyDepositMax"),
                }),
              },
            ]}
            extra={t("systems.Extra.noLimitIfZero")}
          >
            <Input
              type="number"
              placeholder={t("systems.Placeholder.pleaseInput", {
                field: t("domains.Wallet.monthlyDepositMax"),
              })}
            />
          </Form.Item>

          <Form.Item
            label={t("domains.Wallet.note")}
            name="note"
            rules={[
              {
                message: t("systems.Error.cannotBeEmpty", {
                  field: t("domains.Wallet.note"),
                }),
              },
            ]}
          >
            <Input
              placeholder={t("systems.Placeholder.pleaseInput", {
                field: t("domains.Wallet.note"),
              })}
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};
